import { useState } from "react";
import { ENTITY_LABELS } from "../../utils/labelMapping";



export default function useBaseResponsesModal(data, metricType = "") {
  const METRIC_CONFIG = {
  visibility: {
    dataKey: "baseResponses",
    fallbackKey: "baseResponses",
    showResponse: true,
    showPrompt: true,
    titlePrefix: `${ENTITY_LABELS.rank} - Base Prompt and Responses`
  },
  sentiment: {
    dataKey: "completeBaseRes.sentimentQuestionBaseRes",
    showResponse: false,
    showPrompt: true,
    titlePrefix: "Sentiment Question - Base Prompt"
  },
  "sentiment-analysis": {
    dataKey: "completeBaseRes.sentimentAnalysisBasePrompt",
    showResponse: false,
    showPrompt: true,
    isDirectString: true,
    titlePrefix: "Sentiment Analysis - Base Prompt"
  },
  "sentiment-drivers-positive": {
    dataKey: "completeBaseRes.baseSentimentDriverRes",
    showResponse: false,
    showPrompt: true,
    isDirectString: true,
    arrayIndex: 0,
    promptField: "positiveSentimentDriversPrompt",
    titlePrefix: "Positive Sentiment Drivers - Base Prompt"
  },
  "sentiment-drivers-negative": {
    dataKey: "completeBaseRes.baseSentimentDriverRes",
    showResponse: false,
    showPrompt: true,
    isDirectString: true,
    arrayIndex: 1,
    promptField: "negativeSentimentDriversPrompt",
    titlePrefix: "Negative Sentiment Drivers - Base Prompt"
  },
   "core-attributes": {
    showResponse: true,
    showPrompt: true,
    titlePrefix: `${ENTITY_LABELS.brandPillar} - Base Prompt and Responses`
  },
  "category-dimension": {
    showResponse: true,
    showPrompt: true,
    titlePrefix: `${ENTITY_LABELS.purchaseCriteria} - Base Prompt and Responses`
  },
   "signal-strength": {
    showResponse: true,
    showPrompt: true,
    titlePrefix: `${ENTITY_LABELS.brandAttributes} - Base Prompt and Responses`
  },
   "taxonomy": {
    showResponse: true,
    showPrompt: true,
    titlePrefix: `${ENTITY_LABELS.productDimension} - Base Prompt and Responses`
  }
};
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState("");
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalTabs, setInfoModalTabs] = useState(null);
  const [infoModalGlobalContent, setInfoModalGlobalContent] = useState("");
  const productMappingAttribute = data?.productMappingAttribute;
  const hasSections =
    productMappingAttribute &&
    Object.keys(productMappingAttribute).length > 1;

  const getNestedProperty = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  // Get base responses based on metric type using configuration
  const getBaseResponsesByMetric = (metric) => {
    const config = METRIC_CONFIG[metric] || {};

    if (config.isDirectString) {
      const baseData = getNestedProperty(data, config.dataKey);

      if (config.arrayIndex !== undefined && config.promptField) {
        return baseData?.[config.arrayIndex]?.[config.promptField] || "";
      }

      return baseData || "";
    }

    let responses = getNestedProperty(data, config.dataKey);

    if ((!responses || responses.length === 0) && config.fallbackKey) {
      responses = getNestedProperty(data, config.fallbackKey);
    }

    return responses || [];
  };

  //Filter items based on matching criteria (for sentiment questions)
  const filterItemsByMatch = (items, matchCriteria) => {
    if (!matchCriteria) return items;

    const { field = "basePrompt", value } = matchCriteria;

    const matchingItem = items.find(item =>
      item?.[field] && item[field].includes(value)
    );

    return matchingItem ? [matchingItem] : [];
  };

  // Format content based on metric configuration and data structure
  const formatContentByMetric = (items, metric, options = {}) => {
    const config = METRIC_CONFIG[metric] || {};
    const { matchCriteria = null } = options;

    if (config.isDirectString && typeof items === 'string') {
      return `### Base Prompt\n\n${items}`;
    }

    const itemsArray = Array.isArray(items) ? items : [items];

    // Filter items if match criteria provided 
    const filteredItems = filterItemsByMatch(itemsArray, matchCriteria);

    if (filteredItems.length === 0) {
      return `### ${config.titlePrefix}\n\nNo matching data found.`;
    }

    const hasStructuredData = filteredItems.every(
      (item) => item && typeof item === 'object' && item.basePrompt
    );

    if (!hasStructuredData) {
      return filteredItems
        .map((item, idx) => `### Response ${idx + 1}\n\n${item}`)
        .join("\n\n---\n\n");
    }

    const firstPrompt = filteredItems[0].basePrompt;
    const allSamePrompt = filteredItems.every(
      (item) => item.basePrompt === firstPrompt
    );

    if (allSamePrompt) {
      return formatSinglePromptContent(filteredItems, firstPrompt, config);
    } else {
      return formatMultiplePromptsContent(filteredItems, config);
    }
  };

  // Format content when all items share the same prompt
  const formatSinglePromptContent = (items, prompt, config) => {
    let content = "";

    if (config.showPrompt) {
      content = `### Base Prompt\n\n${prompt}`;
    }

    if (config.showResponse && items[0]?.baseResponse) {
      const responsesContent = items
        .map((item, idx) => `**Response ${idx + 1}:**\n\n${item.baseResponse}`)
        .join("\n\n---\n\n");

      content += content ? `\n\n---\n\n### Responses\n\n${responsesContent}`
        : `### Responses\n\n${responsesContent}`;
    }

    return content;
  };

  // Format content when items have different prompts
  const formatMultiplePromptsContent = (items, config) => {
    const groupedByPrompt = items.reduce((acc, item) => {
      const prompt = item.basePrompt;
      if (!acc[prompt]) {
        acc[prompt] = [];
      }
      if (item.baseResponse) {
        acc[prompt].push(item.baseResponse);
      }
      return acc;
    }, {});

    return Object.entries(groupedByPrompt)
      .map(([prompt, responses], queryIdx) => {
        let section = `### Query ${queryIdx + 1}`;

        if (config.showPrompt) {
          section += `\n\n**Base Prompt:**\n\n${prompt}`;
        }

        if (config.showResponse && responses.length > 0) {
          const responsesContent = responses
            .map((response, idx) => `**Response ${idx + 1}:**\n\n${response}`)
            .join("\n\n---\n\n");

          section += `\n\n---\n\n**Responses:**\n\n${responsesContent}`;
        }

        return section;
      })
      .join("\n\n---\n\n");
  };

  // Generic handler for info clicks
  function handleInfoClick(sectionKey, indices, options = {}) {
    const { metric = metricType } = options;
    const baseResponses = getBaseResponsesByMetric(metric);

    if (typeof baseResponses === 'string') {
      const content = formatContentByMetric(baseResponses, metric);
      const config = METRIC_CONFIG[metric] || {};
      setInfoModalContent(content);
      setInfoModalOpen(true);
      return;
    }

    const selectedItems = indices
      .filter((i) => baseResponses[i])
      .map((i) => baseResponses[i]);

    const content = formatContentByMetric(selectedItems, metric);
    const config = METRIC_CONFIG[metric] || {};

    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle all base responses click
  function handleAllBaseResponsesClick(options = {}) {
    const { metric = metricType } = options;
    const baseResponses = getBaseResponsesByMetric(metric);

    if (!baseResponses || (Array.isArray(baseResponses) && baseResponses.length === 0)) {
      return;
    }

    const validItems = Array.isArray(baseResponses)
      ? baseResponses.filter(Boolean)
      : baseResponses;

    const content = formatContentByMetric(validItems, metric);
    const config = METRIC_CONFIG[metric] || METRIC_CONFIG.visibility;

    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix || "Base Responses");
    setInfoModalOpen(true);
  }

  //Handle sentiment question specific info click
  function handleSentimentQuestionClick(questionText) {
    const baseResponses = getBaseResponsesByMetric("sentiment");
    const content = formatContentByMetric(baseResponses, "sentiment", {
      matchCriteria: { field: "basePrompt", value: questionText }
    });

    const config = METRIC_CONFIG.sentiment;
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle sentiment analysis (explanation) info click
  function handleSentimentAnalysisClick() {
    const analysisPrompt = getBaseResponsesByMetric("sentiment-analysis");
    const content = formatContentByMetric(analysisPrompt, "sentiment-analysis");

    const config = METRIC_CONFIG["sentiment-analysis"];
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle positive sentiment drivers info click
  function handlePositiveSentimentDriversClick() {
    const driversPrompt = getBaseResponsesByMetric("sentiment-drivers-positive");
    const content = formatContentByMetric(driversPrompt, "sentiment-drivers-positive");

    const config = METRIC_CONFIG["sentiment-drivers-positive"];
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle negative sentiment drivers info click
  function handleNegativeSentimentDriversClick() {
    const driversPrompt = getBaseResponsesByMetric("sentiment-drivers-negative");
    const content = formatContentByMetric(driversPrompt, "sentiment-drivers-negative");

    const config = METRIC_CONFIG["sentiment-drivers-negative"];
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

function handleCoreAttributeClick(brandName, baseResponsesArray) {
  if (!baseResponsesArray || !Array.isArray(baseResponsesArray)) return;

  const brandData = baseResponsesArray.find(
    item => item?.brandName?.toLowerCase() === brandName?.toLowerCase()
  );
  if (!brandData) return;

  console.log('brandData====>', brandData)

  const config = METRIC_CONFIG["core-attributes"];
  const isMultiPrompting = brandData.multi_prompting === true;
  const isMultiRun = Array.isArray(brandData.baseResponse) && brandData.baseResponse[0]?.runs
  // Multi-prompting: base prompt global, one tab per run with per-attribute responses
  if ((isMultiPrompting && isMultiRun) || isMultiRun) {
    const numRuns = brandData.baseResponse[0].runs.length;

    const globalContent = brandData.basePrompt
      ? `### Base Prompt\n\n${brandData.basePrompt}`
      : "";

    const tabs = Array.from({ length: numRuns }, (_, i) => {
      const runNum = i + 1;
      let tabContent = `### Base Responses — Run ${runNum}\n\n`;
      brandData.baseResponse.forEach((attrEntry) => {
        const run = attrEntry.runs?.find(r => r.run === runNum);
        if (run?.baseRes) {
          tabContent += `**${attrEntry.attribute}:**\n\n${run.baseRes}\n\n---\n\n`;
        }
      });
      return { label: `Run ${runNum}`, content: tabContent };
    });

    setInfoModalGlobalContent(globalContent);
    setInfoModalTabs(tabs);
    setInfoModalContent("");
    setInfoModalTitle(`${config.titlePrefix} - ${brandData.brandName}`);
    setInfoModalOpen(true);
    return;
  }

  // Single-run: existing format
  setInfoModalTabs(null);
  setInfoModalGlobalContent("");
  let content = "";

  if (config.showPrompt && brandData.basePrompt) {
    content = `### Base Prompt\n\n${brandData.basePrompt}`;
  }

  if (config.showResponse && brandData.baseResponse && Array.isArray(brandData.baseResponse)) {
    const responseCount = brandData.baseResponse.length;
    const responsesContent = brandData.baseResponse
      .map((item, idx) =>
        responseCount > 1
          ? `**Response ${idx + 1}:**\n\n${item.baseRes || ""}`
          : item.baseRes || ""
      )
      .join("\n\n---\n\n");

    content += content
      ? `\n\n---\n\n### Base Responses\n\n${responsesContent}`
      : `### Base Responses\n\n${responsesContent}`;
  }

  setInfoModalContent(content);
  setInfoModalTitle(`${config.titlePrefix} - ${brandData.brandName}`);
  setInfoModalOpen(true);
}

function handleCategoryDimensionClick(brandName, entity, iswebSearchLLM, baseResponsesData, options = {}) {
  const { forceFilterByBrand = false } = options;

  if (!baseResponsesData || typeof baseResponsesData !== 'object') return;

  const config = METRIC_CONFIG["category-dimension"];
  const isMainBrand = brandName?.toLowerCase() === entity?.toLowerCase();
  const isMultiPrompting = baseResponsesData.multi_prompting === true;

  // Multi-prompting: criteria-gen + base prompt global, one tab per run with per-criteria responses
  // if (isMultiPrompting) {
    const criteriaRatingData = baseResponsesData.criteriaRatingBaseRes;
    const ratingData = Array.isArray(criteriaRatingData)
      ? criteriaRatingData.find(item => item?.brand?.toLowerCase() === brandName?.toLowerCase())
      : null;
    const isMultiRun = ratingData && Array.isArray(ratingData.criteria) && ratingData.criteria[0]?.runs  
    if ((isMultiPrompting && isMultiRun)|| isMultiRun) {
      const numRuns = ratingData.criteria[0].runs.length;

      let globalContent = "";
      if (isMainBrand) {
        const criteriaGeneration = baseResponsesData.criteriaGenerationBaseRes;
        if (Array.isArray(criteriaGeneration) && criteriaGeneration.length > 0) {
          criteriaGeneration.forEach(item => {
            if (item?.basePrompt) globalContent += `## Criteria Generation\n\n### Base Prompt\n\n${item.basePrompt}\n\n`;
            if (item?.baseRes) globalContent += `---\n\n### Base Response\n\n${item.baseRes}\n\n---\n\n`;
          });
        }
      }
      if (ratingData.basePrompt) {
        globalContent += `## Criteria Rating\n\n### Base Prompt\n\n${ratingData.basePrompt}`;
      }

      const tabs = Array.from({ length: numRuns }, (_, i) => {
        const runNum = i + 1;
        let tabContent = `### Criteria Rating Responses — Run ${runNum}\n\n`;
        ratingData.criteria.forEach(criteriaEntry => {
          const run = criteriaEntry.runs?.find(r => r.run === runNum);
          if (run?.baseRes) {
            tabContent += `**${criteriaEntry.criteria}:**\n\n${run.baseRes}\n\n---\n\n`;
          }
        });
        return { label: `Run ${runNum}`, content: tabContent };
      });

      setInfoModalGlobalContent(globalContent);
      setInfoModalTabs(tabs);
      setInfoModalContent("");
      setInfoModalTitle(`${config.titlePrefix} - ${brandName}`);
      setInfoModalOpen(true);
      return;
    }
  // }

  // Single-run: existing logic
  setInfoModalTabs(null);
  setInfoModalGlobalContent("");
  let content = "";

  // Helper function to format responses
  const formatResponses = (responses) => {
    if (!Array.isArray(responses)) return responses;
    
    const responseCount = responses.length;
    return responses
      .map((item, idx) => {
        const responseText = typeof item === 'string' 
          ? item 
          : (item.baseRes || item.baseResponse || "");
        
        return responseCount > 1 
          ? `**Response ${idx + 1}:**\n\n${responseText}` 
          : responseText;
      })
      .join("\n\n---\n\n");
  };

  // Helper function to add Query section
  let queryCounter = 0;

  const addQuerySection = (data, queryLabel = null) => {
    if (!data) return;

    queryCounter++;

    // Add separator if content exists
    if (content) content += `\n\n---\n\n`;

    // Add query heading when label is provided
    if (queryLabel) {
      content += `## Query ${queryCounter}: ${queryLabel}\n\n`;
    }

    // Add base prompt
    if (config.showPrompt && data.basePrompt) {
      content += `### Base Prompt\n\n${data.basePrompt}\n\n`;
    }

    // Add base response
    if (config.showResponse) {
      const baseResContent = data.baseRes
        ? (Array.isArray(data.baseRes) ? data.baseRes.join("\n\n") : data.baseRes)
        : (data.baseResponse
            ? (Array.isArray(data.baseResponse) ? data.baseResponse : data.baseResponse)
            : "");

      if (baseResContent) {
        const isCriteriaRating = queryLabel === 'Criteria Rating';
        content += `---\n\n### Base Response${isCriteriaRating ? 's' : ''}\n\n`;
        content += isCriteriaRating ? formatResponses(baseResContent) : baseResContent;
      }
    }
  };

  // Criteria Generation (only for main brand)
  if (isMainBrand) {
    const criteriaGeneration = baseResponsesData.criteriaGenerationBaseRes;
    if (Array.isArray(criteriaGeneration) && criteriaGeneration.length > 0) {
      criteriaGeneration.forEach((item) => {
        if (item) addQuerySection(item, 'Criteria Generation');
      });
    }
  }

  // Criteria Rating
  const criteriaRating = baseResponsesData.criteriaRatingBaseRes;

  if (criteriaRating && Array.isArray(criteriaRating)) {
    if (iswebSearchLLM) {
      const ratingData = criteriaRating.find(item => item?.brand?.toLowerCase() === brandName?.toLowerCase());
      if (ratingData) {
        addQuerySection(ratingData, 'Criteria Rating');
      }
    } else if (forceFilterByBrand) {
      const brandItems = criteriaRating.filter(item =>
        item?.basePrompt?.toLowerCase().includes(brandName?.toLowerCase())
      );
      brandItems.forEach((item) => {
        if (item) addQuerySection(item, 'Criteria Rating');
      });
    } else {
      criteriaRating.forEach((item) => {
        if (item) addQuerySection(item, 'Criteria Rating');
      });
    }
  }

  // Set modal content and open
  setInfoModalContent(content || "No data available for this brand.");
  setInfoModalTitle(`${config.titlePrefix} - ${brandName}`);
  setInfoModalOpen(true);
}

function handleSignalStrengthClick(iswebSearchLLM, baseResponsesData,type="brand") {
  const config = METRIC_CONFIG["signal-strength"];

  const formatBaseResults = (results) => {
    if (!results || !Array.isArray(results)) return "";
    const count = results.length;
    return results
      .map((item, idx) => {
        const text = typeof item === 'string' ? item : (item.baseRes || "");
        return count > 1 ? `**Response ${idx + 1}:**\n\n${text}` : text;
      })
      .join("\n\n---\n\n");
  };

  // CASE: array of competitors (AttributeOverview - single info icon for all)
  if (Array.isArray(baseResponsesData)) {
    if (!baseResponsesData || baseResponsesData.length === 0) return;

    let content = "";

    baseResponsesData.forEach((competitor, competitorIdx) => {
      const brandName = competitor.brandName || competitor.name || `Competitor ${competitorIdx + 1}`;
      const baseResponse = competitor.baseResponses || competitor.baseResponse || {};

      // Section separator between competitors
      if (content) content += `\n\n${"=".repeat(60)}\n\n`;

      if (iswebSearchLLM) {
        // Query 1: Positive Attributes
        if (baseResponse.positiveAttributePrompt) {
          content += `## Query 1: Positive Attributes\n\n`;
          content += `### Base Prompt for ${brandName}\n\n${baseResponse.positiveAttributePrompt}\n\n`;

          const positiveContent = formatBaseResults(baseResponse.positiveBaseResults);
          if (positiveContent) {
            content += `---\n\n### Base Responses\n\n${positiveContent}`;
          }
        }

        // Query 2: Negative Attributes
        if (baseResponse.negativeAttributePrompt) {
          content += `\n\n---\n\n## Query 2: Negative Attributes\n\n`;
          content += `### Base Prompt for ${brandName}\n\n${baseResponse.negativeAttributePrompt}\n\n`;

          const negativeContent = formatBaseResults(baseResponse.negativeBaseResults);
          if (negativeContent) {
            content += `---\n\n### Base Responses\n\n${negativeContent}`;
          }
        }
      } else {
        // Non-Web Search LLM: basePrompt + baseRes array
        const baseResArray = Array.isArray(baseResponse.baseRes)
          ? baseResponse.baseRes
          : baseResponse;

        if (baseResponse.basePrompt) {
          content += `### Base Prompt for ${brandName}\n\n${baseResponse.basePrompt}\n\n`;
        }

        const baseResContent = formatBaseResults(baseResArray);
        if (baseResContent) {
          content += `---\n\n### Base Responses\n\n${baseResContent}`;
        }
      }
    });
    const title = type === "brand"? ENTITY_LABELS.brand :ENTITY_LABELS.product

    setInfoModalContent(content || "No data available.");
    setInfoModalTitle(`${title} — All Brands`);
    setInfoModalOpen(true);
    return;
  }

  // CASE: single object (existing behavior - Attributes.js single brand icon)
  if (!baseResponsesData || typeof baseResponsesData !== 'object') return;

  let content = "";

  if (iswebSearchLLM) {
    // Query 1: Positive Attributes
    if (baseResponsesData.positiveAttributePrompt) {
      content += `## Query 1: Positive Attributes\n\n`;
      content += `### Base Prompt\n\n${baseResponsesData.positiveAttributePrompt}\n\n`;

      const positiveContent = formatBaseResults(baseResponsesData.positiveBaseResults);
      if (positiveContent) {
        content += `---\n\n### Base Responses\n\n${positiveContent}`;
      }
    }

    // Query 2: Negative Attributes
    if (baseResponsesData.negativeAttributePrompt) {
      if (content) content += `\n\n---\n\n`;
      content += `## Query 2: Negative Attributes\n\n`;
      content += `### Base Prompt\n\n${baseResponsesData.negativeAttributePrompt}\n\n`;

      const negativeContent = formatBaseResults(baseResponsesData.negativeBaseResults);
      if (negativeContent) {
        content += `---\n\n### Base Responses\n\n${negativeContent}`;
      }
    }
  } else {
    // Non-Web Search LLM: basePrompt + baseRes array
    const baseResData = Array.isArray(baseResponsesData.baseRes)
      ? baseResponsesData.baseRes
      : baseResponsesData;

    if (baseResponsesData.basePrompt) {
      content += `### Base Prompt\n\n${baseResponsesData.basePrompt}\n\n`;
    }

    const baseResContent = formatBaseResults(baseResData);
    if (baseResContent) {
      content += `---\n\n### Base Responses\n\n${baseResContent}`;
    }
  }

  setInfoModalContent(content || "No data available.");
  setInfoModalTitle(config.titlePrefix);
  setInfoModalOpen(true);
}

function handleTaxonomyClick(brandName, entity, baseResponsesData) {
  if (!baseResponsesData || typeof baseResponsesData !== 'object') {
    return;
  }

  const config = METRIC_CONFIG["taxonomy"];
  const isMainBrand = brandName?.toLowerCase() === entity?.toLowerCase();
  let content = "";

  // Helper to format baseRes (handles JSON string or plain string)
  const formatBaseRes = (baseRes) => {
    if (!baseRes) return "";
    if (typeof baseRes !== 'string') return JSON.stringify(baseRes, null, 2);
    try {
      const parsed = JSON.parse(baseRes);
      return "```json\n" + JSON.stringify(parsed, null, 2) + "\n```";
    } catch {
      return baseRes;
    }
  };

  // Query 1: Attribute Generation — only for main brand
  if (isMainBrand) {
    const attributeGeneration = baseResponsesData.ProductDimensionAttribute;
    const generationData = Array.isArray(attributeGeneration)
      ? attributeGeneration[0]
      : attributeGeneration;

    if (generationData) {
      content += `## Query 1: Attribute Generation\n\n`;

      if (config.showPrompt && generationData.basePrompt) {
        content += `### Base Prompt\n\n${generationData.basePrompt}\n\n`;
      }

      if (config.showResponse && generationData.baseRes) {
        const resText = Array.isArray(generationData.baseRes)
          ? generationData.baseRes.map((r) => JSON.stringify(r, null, 2)).join("\n\n")
          : formatBaseRes(generationData.baseRes);
        content += `---\n\n### Base Response\n\n${resText}`;
      }
    }
  }

  // Query 2 (main brand) / Only section (other brands): Attribute Rating
  const ratingArray = baseResponsesData.baseRatingRes;

  if (Array.isArray(ratingArray)) {
    const ratingData = ratingArray.find(
      (item) => item?.brandName?.toLowerCase() === brandName?.toLowerCase()
    );

    if (ratingData) {
      if (content) content += `\n\n---\n\n`;

      if (isMainBrand) {
        content += `## Query 2: Attribute Rating\n\n`;
      }

      if (config.showPrompt && ratingData.basePrompt) {
        content += `### Base Prompt\n\n${ratingData.basePrompt}\n\n`;
      }

      if (config.showResponse && ratingData.baseRes) {
        content += `---\n\n### Base Response\n\n${formatBaseRes(ratingData.baseRes)}`;
      }
    }
  }

  setInfoModalContent(content || "No data available for this brand.");
  setInfoModalTitle(`${config.titlePrefix} - ${brandName}`);
  setInfoModalOpen(true);
}

  function closeInfoModal() {
    setInfoModalOpen(false);
  }

  return {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    infoModalTabs,
    infoModalGlobalContent,
    hasSections,
    productMappingAttribute,
    closeInfoModal,
    handleInfoClick,
    handleAllBaseResponsesClick,
    handleSentimentQuestionClick,
    handleSentimentAnalysisClick,
    handlePositiveSentimentDriversClick,
    handleNegativeSentimentDriversClick,
    handleCoreAttributeClick,
    handleCategoryDimensionClick,
    handleSignalStrengthClick,
    handleTaxonomyClick
  };
}